import { useState } from 'react'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import { Label } from '@/components/ui/label'
import { PhoneVerifyInput } from '@/features/auth/components/shared/phone-verify-input'
import { usePhone } from '@/features/auth/hooks/use-phone'
import { useAuthStore } from '@/features/auth/store/auth-store'

const otpSchema = z
  .string()
  .length(6, '请输入 6 位验证码')
  .regex(/^\d+$/, '验证码必须为数字')

export function ChangePhoneForm({ trigger }: { trigger?: React.ReactNode }) {
  const { usersBindPhoneMutationAsync } = usePhone()
  const { handleBindPhone } = useAuthStore((s) => s.handlers)
  const open = useAuthStore((s) => s.changePhoneDialog.open)
  const setOpen = useAuthStore((s) => s.changePhoneDialog.setOpen)

  const [phoneNumber, setPhoneNumber] = useState('')
  const [otp, setOtp] = useState('')
  const [otpError, setOtpError] = useState('')
  const [isCodeSent, setIsCodeSent] = useState(false)
  const [loading, setLoading] = useState(false)

  const onSubmit = async () => {
    if (!phoneNumber || !otp) return

    // Zod 验证 OTP
    const otpResult = otpSchema.safeParse(otp)
    if (!otpResult.success) {
      setOtpError(otpResult.error.issues[0].message)
      return
    }
    setOtpError('')

    setLoading(true)
    try {
      await handleBindPhone(phoneNumber, otp, usersBindPhoneMutationAsync)
      // 如果绑定成功，通常会关闭弹窗，这个在 handleBindPhone 里处理了 (通过 store state)
    } catch {
      // 错误已在 store handler 中处理
    } finally {
      setLoading(false)
    }
  }

  // 当弹窗关闭时重置状态
  const onOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    if (!newOpen) {
      setPhoneNumber('')
      setOtp('')
      setOtpError('')
      setIsCodeSent(false)
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        {trigger || <Button variant='outline'>绑定/更换手机号</Button>}
      </DialogTrigger>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>绑定手机号</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          绑定手机号后，您可以通过手机验证码直接登录，账号更加安全。
        </DialogDescription>
        <div className='grid gap-6 py-4'>
          {/* 手机号输入与发送验证码 */}
          <div className='grid gap-2'>
            <Label>新手机号</Label>
            <PhoneVerifyInput
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              onCodeSent={() => setIsCodeSent(true)}
              type='bind'
            />
          </div>

          {/* 验证码输入 */}
          {isCodeSent && (
            <div className='grid gap-2'>
              <Label htmlFor='otp'>验证码</Label>
              <div className='flex items-center gap-2'>
                <InputOTP
                  maxLength={6}
                  value={otp}
                  onChange={(v) => {
                    setOtp(v)
                    if (otpError) setOtpError('')
                  }}
                  containerClassName='flex-1'
                >
                  <InputOTPGroup className='w-full'>
                    <InputOTPSlot className='flex-1' index={0} />
                    <InputOTPSlot className='flex-1' index={1} />
                    <InputOTPSlot className='flex-1' index={2} />
                    <InputOTPSlot className='flex-1' index={3} />
                    <InputOTPSlot className='flex-1' index={4} />
                    <InputOTPSlot className='flex-1' index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {otpError && (
                <p className='text-destructive text-xs'>{otpError}</p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            type='submit'
            className='w-full'
            disabled={!isCodeSent || otp.length < 6 || loading}
            onClick={onSubmit}
          >
            {loading ? '正在绑定...' : '确认绑定'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
